// configWidgets — small presentational components extracted verbatim from
// WebshopConfigScreen.js. Stateless (or memoised) building blocks with no
// screen-state dependencies, so the monolith holds only the screen component.
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { getContrastText } from './configHelpers';

// Children may be a FUNCTION: it's only invoked while the panel is active.
// Plain JSX children are evaluated eagerly by the parent even when the tab is
// hidden (React builds the element tree before TabPanel can gate it) — for the
// heavy config tabs that made every keystroke pay for all five tabs.
export const TabPanel = ({ children, value, index, sx }) => (
    <div role="tabpanel" hidden={value !== index}>
        {value === index && (
            <Box sx={{ pt: 3, ...sx }}>
                {typeof children === 'function' ? children() : children}
            </Box>
        )}
    </div>
);
TabPanel.propTypes = { children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]), value: PropTypes.number.isRequired, index: PropTypes.number.isRequired, sx: PropTypes.object };

// Shared "Save" button for the config dialogs (style config/colour + Pages block
// gear). Runs the global saveConfig, shows a spinner + "saving…" while in flight,
// and closes the dialog on success (only when a save wasn't already running when
// it was clicked). Previously this exact block was copy-pasted 3×.
export const ConfigSaveButton = ({ saving, onSave, onClose, t }) => (
    <Button
        variant="contained"
        disabled={saving}
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        onClick={async () => {
            const wasSaving = saving;
            await onSave();
            if (!wasSaving && onClose) onClose();
        }}
    >
        {saving ? (t ? t('cfg_saving', 'Bezig met opslaan…') : 'Bezig met opslaan…') : (t ? t('save', 'Save') : 'Save')}
    </Button>
);
ConfigSaveButton.propTypes = { saving: PropTypes.bool, onSave: PropTypes.func, onClose: PropTypes.func, t: PropTypes.func };

// Standalone component so it keeps a stable identity across parent re-renders —
// critical because the native OS color picker closes if its <input> gets
// unmounted/remounted while the user is dragging. By defining this at module
// scope, React reconciles the same DOM node across renders and the picker stays
// open.
export const ColorField = React.memo(function ColorField({ label, value, onChange, mutedText, borderColor, markPickerInteraction, compact = false }) {
    const contrastColor = getContrastText(value);
    // Compact mode shrinks the swatch + hex input for use in the sidebar
    // panels, where vertical space is at a premium.
    const swatchHeight = compact ? 30 : 44;
    const hexFontSize  = compact ? '0.78rem' : '0.95rem';
    const hexWidth     = compact ? 96 : 120;
    return (
        <Box>
            <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: compact ? 0.25 : 0.5, fontSize: compact ? '0.72rem' : undefined }}>
                {label}
            </Typography>
            <Box
                component="label"
                sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: swatchHeight,
                    backgroundColor: value,
                    borderRadius: 1,
                    border: `1px solid ${borderColor}`,
                    cursor: 'pointer',
                    overflow: 'hidden',
                    '&:hover .hex-input, & .hex-input:focus': {
                        color: contrastColor,
                    },
                }}
                onMouseDown={markPickerInteraction}
            >
                <input
                    type="color"
                    value={value}
                    onChange={(e) => { markPickerInteraction && markPickerInteraction(); onChange(e.target.value); }}
                    onInput={markPickerInteraction}
                    onBlur={markPickerInteraction}
                    style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', zIndex: 1 }}
                />
                <Box
                    component="input"
                    className="hex-input"
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.target.select()}
                    sx={{
                        position: 'relative',
                        zIndex: 2,
                        background: 'transparent',
                        border: 'none',
                        outline: 'none',
                        color: value,
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        fontSize: hexFontSize,
                        letterSpacing: 1,
                        textAlign: 'center',
                        width: hexWidth,
                        cursor: 'text',
                        transition: 'color 150ms',
                    }}
                />
            </Box>
        </Box>
    );
});
